import { useEffect } from 'react';
import { Alert, Form, Input, Modal, Space, Typography } from 'antd';

import type { TemplateSlotEntry } from '../schema';

const { Text } = Typography;

interface BlockSettingsModalProps {
  open: boolean;
  entry: TemplateSlotEntry | null;
  onSubmit: (entry: TemplateSlotEntry) => void;
  onCancel: () => void;
  error?: string | null;
}

interface BlockSettingsFormValues {
  key: string;
  area?: string;
  when?: string;
  props?: string;
}

const normalizeJson = (value: unknown) => {
  if (value == null) {
    return '';
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return '';
  }
};

const serializeCondition = (condition: TemplateSlotEntry['when']) => {
  if (!condition) {
    return '';
  }
  if (typeof condition === 'string') {
    return condition;
  }
  return JSON.stringify(condition, null, 2);
};

const parseCondition = (raw: string): TemplateSlotEntry['when'] => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed) as TemplateSlotEntry['when'];
  } catch (error) {
    return trimmed;
  }
};

const parseProps = (raw: string): TemplateSlotEntry['props'] => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    throw new Error('Las props deben ser un objeto JSON.');
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('No se pudo parsear el JSON de props.');
  }
};

const BlockSettingsModal = ({ open, entry, onSubmit, onCancel, error }: BlockSettingsModalProps) => {
  const [form] = Form.useForm<BlockSettingsFormValues>();

  useEffect(() => {
    if (open && entry) {
      // Small timeout ensures the form inside Modal is mounted before setting values
      const timer = setTimeout(() => {
        form.setFieldsValue({
          key: entry.key ?? '',
          area: entry.area,
          when: serializeCondition(entry.when),
          props: normalizeJson(entry.props),
        });
      }, 0);
      return () => clearTimeout(timer);
    } else {
      // Do not reset fields here if the modal handles destruction, 
      // effectively unmounting the form. Resetting on unmounted form causes warning.
      // form.resetFields(); 
    }
  }, [open, entry, form]);

  const handleOk = () => {
    void form
      .validateFields()
      .then((values) => {
        if (!entry) {
          return;
        }
        try {
          const next: TemplateSlotEntry = {
            ...entry,
            key: values.key?.trim() ?? entry.key,
            area: values.area?.trim() || undefined,
            when: parseCondition(values.when ?? ''),
            props: parseProps(values.props ?? ''),
          };
          onSubmit(next);
        } catch (submitError) {
          const message = submitError instanceof Error ? submitError.message : 'Error al procesar el formulario.';
          form.setFields([
            {
              name: 'props',
              errors: [message],
            },
          ]);
        }
      })
      .catch(() => undefined);
  };

  return (
    <Modal
      open={open}
      title={entry ? `Configurar bloque «${entry.block}»` : 'Configurar bloque'}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Guardar"
      cancelText="Cancelar"
      destroyOnHidden
      width={560}
    >
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Text type="secondary">
            Editá la clave única del bloque, el área destino (para agrupar en la tarjeta o header), las condiciones
            de renderizado y las props adicionales (JSON).
          </Text>
        </div>

        {error ? <Alert type="error" showIcon title={error} /> : null}

        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="key"
            label="Clave del bloque"
            rules={[{ required: true, message: 'La clave es obligatoria.' }]}
          >
            <Input placeholder="Ej: price" autoFocus />
          </Form.Item>

          <Form.Item name="area" label="Área (opcional)">
            <Input placeholder="Ej: info, footer, brand" />
          </Form.Item>

          <Form.Item name="when" label="Condición (string o JSON)">
            <Input.TextArea rows={3} placeholder="Ej: hasResults o { 'not': 'hasResults' }" />
          </Form.Item>

          <Form.Item name="props" label="Props adicionales (JSON)">
            <Input.TextArea rows={6} placeholder="{ 'align': 'center' }" />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};

export default BlockSettingsModal;
